import React from 'react';
import { ArrowRight } from 'lucide-react';
import './IntelligenceBriefing.css';

const BRIEFING_PDF_BASE = '/intelligence-briefings';

const TICKER_ITEMS = [
  { dot: 'green', label: 'MYANMAR', text: 'Min Aung Hlaing chooses India for first presidential foreign visit · rare earths joint statement signed June 1' },
  { dot: 'green', label: 'INDONESIA', text: '8th India-Indonesia JCM in New Delhi · critical minerals, maritime security, defence on the agenda' },
  { dot: 'amber', label: 'ASEAN TRADE', text: 'India ~$120B vs China-ASEAN approaching $1T · AITIGA review stuck after 10 rounds' },
  { dot: 'red', label: 'KACHIN', text: 'Heavy REE deposits contested since Oct 2024 · ORF places commercial access at 2028–2035 earliest' },
  { dot: 'amber', label: 'MODI VISIT', text: 'July state visit to Jakarta next · nickel processing partnership still unconfirmed' },
  { dot: 'blue', label: 'OBSERVATORY', text: 'India-UK Critical Minerals Supply Chain Observatory launched in New Delhi · June 4' },
  { dot: 'red', label: 'MOFCOM', text: 'April 2025 rare earth licensing still active · Reuters confirms delays persist · June 10' },
  { dot: 'amber', label: 'USCBC SURVEY', text: '47% of 38 affected firms seeking non-China REE suppliers have not found alternatives' },
  { dot: 'amber', label: 'REPM', text: 'India targets first rare earth permanent magnet output by end-2026 · beneficiary selection pending' },
  { dot: 'blue', label: 'META-RELIANCE', text: 'Meta to lease 168 MW AI-ready data centre Reliance builds in Jamnagar' },
  { dot: 'blue', label: 'AIRTRUNK', text: '$30B India investment plan · 5 GW data centre capacity by 2030 · 600 MW in pipeline' },
  { dot: 'green', label: 'HYDERABAD', text: '~1.09 GW installed IT load in 2026 · Telangana targets 5 GW by 2030' },
  { dot: 'blue', label: 'UPC-VOLT', text: '100 MW AI data centre MoU at Bharat Future City · Rs 5,000 crore · dedicated renewable plant' },
  { dot: 'green', label: 'SEMICON', text: 'Micron ATMP and Kaynes OSAT in commercial production · 2 new Gujarat projects approved May 2026' },
  { dot: 'amber', label: 'INDIAAI', text: '38,000+ GPUs onboarded on IndiaAI compute portal · advanced chips still externally dependent' },
  { dot: 'amber', label: 'US-CHINA THAW', text: 'India building eastern flank value independent of shared China concern · CFR warns on South China Sea' },
];

const ISSUE_2_BRIEFINGS = [
  {
    id: 'eastern',
    cardClass: 'eastern-card',
    coverClass: 'eastern',
    pdfUrl: '',
    editionPdfs: {
      POLICY: '',
      CFO: '',
    },
    views: ['POLICY', 'CFO'],
    kicker: "India's Eastern Engagement",
    headline: 'India Builds the Eastern Flank',
    deck: "Myanmar's president chose India for his first foreign visit. Four days later, India and Indonesia convened their Joint Commission. Both engagements build strategic value that survives a US-China thaw.",
    highlights: [
      'Myanmar joint statement on rare earths — but Kachin deposits remain contested',
      'Indonesia JCM: critical minerals and maritime security, no nickel deal yet',
      'India-ASEAN trade ~$120B vs China-ASEAN approaching $1T',
    ],
    date: 'June 9, 2026 · 5-min read',
  },
  {
    id: 'minerals-june',
    cardClass: 'minerals-june-card',
    coverClass: 'minerals-june',
    pdfUrl: '',
    views: ['MINERALS'],
    kicker: 'Critical Minerals Intelligence',
    headline: 'From Securing Minerals to Watching Supply Chains',
    deck: "The India-UK observatory launched June 4 adds the intelligence layer India's critical minerals policy stack was missing. China's licensing controls remain active — and 47% of affected firms still cannot find non-Chinese alternatives.",
    highlights: [
      'MOFCOM licensing still biting · diplomatic easing is not supply assurance',
      'India-UK Critical Minerals Global Supply Chain Observatory live',
      'REPM beneficiary selection and corridor execution are the lead indicators',
    ],
    date: 'June 10, 2026 · 5-min read',
  },
  {
    id: 'tech',
    cardClass: 'tech-card',
    coverClass: 'tech',
    pdfUrl: '',
    views: ['TECH INFRA'],
    kicker: 'Technology Infrastructure Intelligence',
    headline: 'The Megawatt Phase',
    deck: 'Meta will lease a 168 MW AI-ready data centre Reliance builds in Jamnagar. AirTrunk plans 5 GW by 2030. India is building compute capacity — the question is whether it is building compute sovereignty.',
    highlights: [
      'Meta-Reliance 168 MW Jamnagar · AirTrunk $30B India plan',
      'Hyderabad ~1.09 GW installed IT load · Telangana targets 5 GW by 2030',
      'Semiconductor midstream advancing · advanced chips and GPUs still external',
    ],
    date: 'June 10, 2026 · 5-min read',
  },
];

const ISSUE_1_BRIEFINGS = [
  {
    id: 'beijing',
    cardClass: 'beijing-card',
    coverClass: 'beijing',
    pdfUrl: `${BRIEFING_PDF_BASE}/great_powers_PolicyMakers_version.pdf`,
    editionPdfs: {
      POLICY: `${BRIEFING_PDF_BASE}/great_powers_PolicyMakers_version.pdf`,
      CFO: `${BRIEFING_PDF_BASE}/great_powers_CFO_version.pdf.pdf`,
    },
    stamp: 'BRIEFING 01',
    views: ['POLICY', 'CFO'],
    kicker: 'Great Power Politics',
    headline: 'Beijing Holds Court',
    deck: 'Within eight days, the leaders of all three great powers converged on Beijing. Xi received both without leaving home. That sequencing is not incidental. It is the signal.',
    highlights: [
      'Trump & Putin both fly to Xi — China becomes the indispensable interlocutor',
      'Decrees 834 & 835: a regulatory landmine for any firm sourcing from China',
      'India’s electronics export surge, the rare-earth choke point, and what shifts after the summit',
    ],
    date: 'May 18, 2026  ·  5-min read',
  },
  {
    id: 'modi',
    cardClass: 'modi-card',
    coverClass: 'modi',
    pdfUrl: `${BRIEFING_PDF_BASE}/modi_visit_PolicyMakers_version.pdf`,
    editionPdfs: {
      POLICY: `${BRIEFING_PDF_BASE}/modi_visit_PolicyMakers_version.pdf`,
      CFO: `${BRIEFING_PDF_BASE}/modi_visit_CFO_version.pdf`,
    },
    stamp: 'BRIEFING 02',
    views: ['POLICY', 'CFO'],
    kicker: 'Great Power Politics',
    headline: 'India Moves West',
    deck: 'Modi’s 5-nation tour is the first major in-person activation of three frameworks India has assembled over 18 months: the India-EU FTA, the India-EFTA TEPA, and IMEC. The five stops map almost exactly onto the corridor’s architecture.',
    highlights: [
      'UAE → Netherlands → Sweden → Norway → Italy: five capitals, six days, three frameworks',
      'India-EU FTA: largest ever, EUR 180B+ market, $33.5B at zero duty',
      'IMEC’s missing middle, the CBAM clock, and the FTA window that closes early 2027',
    ],
    date: 'May 18, 2026  ·  5-min read',
  },
  {
    id: 'minerals',
    cardClass: 'minerals-card',
    coverClass: 'minerals',
    pdfUrl: `${BRIEFING_PDF_BASE}/critical_minerals_intelligence.pdf`,
    stamp: 'Briefing 03',
    views: ['MINERALS'],
    kicker: 'Critical Minerals Intelligence',
    headline: 'What the Dashboard Is Telling Us',
    deck: "Three signals from India's Critical Minerals Research Dashboard that connect directly to the geopolitical events described above — and that the Beijing summit did not resolve.",
    highlights: [
      'REE risk radar still in ACT zone — MOFCOM licensing remains active post-summit',
      'RED alert: China 84–90% import share · India ASR at 24%',
      'Australia high-opportunity · China over-exposed · policy gap until 2027–28',
    ],
    date: 'May 18, 2026  ·  5-min read',
  },
];

function TickerItem({ dot, label, text }) {
  return (
    <span className="ib-ticker-item">
      <span className={`ib-dot ${dot}`} />
      <strong>{label}</strong>
      {text}
    </span>
  );
}

function EditionCtaLink({ href, label }) {
  if (!href) {
    return (
      <span className="ib-briefing-cta ib-briefing-cta--pending">
        {label}
        <ArrowRight aria-hidden="true" />
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="ib-briefing-cta"
    >
      {label}
      <ArrowRight aria-hidden="true" />
    </a>
  );
}

function BriefingCard({
  cardClass,
  coverClass,
  pdfUrl,
  editionPdfs,
  stamp,
  views,
  kicker,
  headline,
  deck,
  highlights,
  date,
}) {
  const hasEditions = editionPdfs?.POLICY !== undefined || editionPdfs?.CFO !== undefined;

  return (
    <div className={`ib-briefing ${cardClass}`}>
      <div className={`ib-briefing-cover ${coverClass}`}>
        {stamp ? <span className="ib-cover-stamp">{stamp}</span> : null}
        <div className="ib-cover-views">
          {views.map((view) => {
            const editionUrl = editionPdfs?.[view];
            if (editionUrl) {
              return (
                <a
                  key={view}
                  href={editionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ib-cover-edition-link"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  {view}
                </a>
              );
            }
            return <span key={view}>{view}</span>;
          })}
        </div>
        <div className="ib-cover-title">
          <div className="ib-cover-kicker">{kicker}</div>
          <h2 className="ib-cover-headline">{headline}</h2>
        </div>
      </div>
      <div className="ib-briefing-body">
        <p className="ib-briefing-deck">{deck}</p>
        <ul className="ib-briefing-highlights">
          {highlights.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="ib-briefing-meta">
          <span className="ib-briefing-date">{date}</span>
          {hasEditions ? (
            <div className="ib-briefing-cta-group">
              <EditionCtaLink href={editionPdfs.POLICY} label="Read Policy" />
              <span className="ib-briefing-cta-sep" aria-hidden="true">
                /
              </span>
              <EditionCtaLink href={editionPdfs.CFO} label="Read CFO" />
            </div>
          ) : (
            <EditionCtaLink href={pdfUrl} label="Read briefing" />
          )}
        </div>
      </div>
    </div>
  );
}

function BriefingGrid({ briefings }) {
  return (
    <div className="ib-briefing-grid">
      {briefings.map((briefing) => (
        <BriefingCard key={briefing.id} {...briefing} />
      ))}
    </div>
  );
}

const IntelligenceBriefing = () => (
  <div className="intelligence-briefing-page min-h-full">
    <div className="ib-ticker" role="marquee" aria-label="Latest headlines from this week's briefings">
      <div className="ib-ticker-tag">News</div>
      <div className="ib-ticker-viewport">
        <div className="ib-ticker-track">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, index) => (
            <TickerItem key={`${item.label}-${index}`} {...item} />
          ))}
        </div>
      </div>
    </div>

    <div className="ib-page">
      <header className="ib-hero">
        <div className="ib-hero-eyebrow">VOL. 1  ·  ISSUE 2  ·  JUNE 10, 2026</div>
        <h1>GEOECONOMICS NEWSLETTER</h1>
        <p className="ib-hero-tag">What Changed the World Last Week</p>
      </header>

      <section className="ib-issues-section">
        <div className="ib-section-label">This Week&apos;s Briefings</div>
        <div className="ib-section-label ib-section-label--issue">Issue 2 — June 10, 2026</div>
        <BriefingGrid briefings={ISSUE_2_BRIEFINGS} />

        <div className="ib-section-label ib-section-label--issue ib-section-label--spaced">Issue 1 — May 18, 2026</div>
        <BriefingGrid briefings={ISSUE_1_BRIEFINGS} />
      </section>

      <footer className="ib-site-footer">
        <div className="ib-site-footer-row">
          <div>GEOECONOMICS · WEEKLY NEWSLETTER</div>
        </div>
      </footer>
    </div>
  </div>
);

export default IntelligenceBriefing;
